import React, { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Plus, Trash2, Loader2 } from 'lucide-react'
import { formatDate } from '@/lib/dateUtils'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { PageHeader } from '@/components/shared'
import { emailSuppressionService } from '@/services/emailSuppressionService'

const reasonVariant = {
  UNSUBSCRIBE: 'secondary',
  HARD_BOUNCE: 'outline',
  SPAM_COMPLAINT: 'outline',
  SOFT_BOUNCE: 'outline',
  MANUAL: 'secondary',
}

const Suppression = () => {
  const queryClient = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [reason, setReason] = useState('MANUAL')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [deletingEntry, setDeletingEntry] = useState(null)

  const { data, isLoading } = useQuery('email-suppression', emailSuppressionService.getAllSuppressions, {
    onError: () => toast.error('Failed to fetch suppression list'),
  })
  const suppressions = data?.data?.suppressions || []

  const refresh = () => queryClient.invalidateQueries('email-suppression')

  const handleAdd = async () => {
    if (!email.trim()) {
      toast.error('Email is required')
      return
    }
    setSaving(true)
    try {
      await emailSuppressionService.createSuppression({ email, reason, notes })
      toast.success('Email added to suppression list')
      setAddOpen(false)
      setEmail('')
      setReason('MANUAL')
      setNotes('')
      refresh()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add to suppression list')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deletingEntry) return
    try {
      await emailSuppressionService.deleteSuppression(deletingEntry.id)
      toast.success('Removed from suppression list')
      refresh()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to remove suppression entry')
    } finally {
      setDeletingEntry(null)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Suppression List"
        description="Emails that are excluded from all campaigns — either because they unsubscribed, bounced, complained, or were added manually"
        actions={
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Email
          </Button>
        }
      />

      <Card>
        <div className="rounded-lg border-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="bg-muted/50 font-medium">Email</TableHead>
                <TableHead className="bg-muted/50 font-medium">Reason</TableHead>
                <TableHead className="bg-muted/50 font-medium">Campaign</TableHead>
                <TableHead className="bg-muted/50 font-medium">Notes</TableHead>
                <TableHead className="bg-muted/50 font-medium">Added</TableHead>
                <TableHead className="bg-muted/50 font-medium text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(4)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(6)].map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}
                  </TableRow>
                ))
              ) : suppressions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                    No suppressed emails yet. Addresses land here automatically from bounces, complaints and unsubscribes.
                  </TableCell>
                </TableRow>
              ) : (
                suppressions.map((s) => (
                  <TableRow key={s.id} className="hover:bg-muted/50 border-border">
                    <TableCell className="font-medium">{s.email}</TableCell>
                    <TableCell><Badge variant={reasonVariant[s.reason] || 'secondary'}>{s.reason}</Badge></TableCell>
                    <TableCell>{s.campaign?.name || '—'}</TableCell>
                    <TableCell className="max-w-xs truncate text-muted-foreground">{s.notes || '—'}</TableCell>
                    <TableCell>{formatDate(s.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDeletingEntry(s)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add to Suppression List</DialogTitle>
            <DialogDescription>This email will be excluded from all future campaigns.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="someone@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MANUAL">Manual</SelectItem>
                  <SelectItem value="UNSUBSCRIBE">Unsubscribe</SelectItem>
                  <SelectItem value="HARD_BOUNCE">Hard Bounce</SelectItem>
                  <SelectItem value="SOFT_BOUNCE">Soft Bounce</SelectItem>
                  <SelectItem value="SPAM_COMPLAINT">Spam Complaint</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Notes (optional)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Why is this being suppressed?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletingEntry} onOpenChange={() => setDeletingEntry(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from Suppression List</AlertDialogTitle>
            <AlertDialogDescription>
              "{deletingEntry?.email}" will become eligible to receive campaigns again. Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default Suppression
